import { useState, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, ArrowRight, AlertTriangle, CheckCircle, Loader2, Search, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient as qc } from "@/lib/queryClient";
import type { Pulse, PulseMoveValidationResult } from "@shared/schema";
import { cn } from "@/lib/utils";

interface PulseSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pulse: Pulse;
  isAdmin?: boolean;
}

interface RecentProject {
  id: string;
  projectName: string;
  clientName: string;
}

export function PulseSettingsDialog({
  open,
  onOpenChange,
  pulse,
  isAdmin = false,
}: PulseSettingsDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [targetProjectId, setTargetProjectId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMoveConfirm, setShowMoveConfirm] = useState(false);

  // Fetch recent active projects for move target selection
  const { data: recentProjects = [], isLoading: loadingProjects } = useQuery<RecentProject[]>({
    queryKey: ['/api/projects/recent'],
    enabled: open,
  });

  // Search projects when query changes
  const { data: searchResults = [], isLoading: loadingSearch } = useQuery<RecentProject[]>({
    queryKey: ['/api/projects/search', { q: searchQuery }],
    enabled: open && searchQuery.length >= 2,
  });

  // Fetch validation when target project is selected (re-fetches for each target)
  const { data: validation, isLoading: loadingValidation, isError: validationError, error: validationErrorDetails, refetch: refetchValidation } = useQuery<PulseMoveValidationResult>({
    queryKey: ['/api/pulses', pulse.id, 'move-validation', { targetProjectId }],
    queryFn: async () => {
      const url = `/api/pulses/${pulse.id}/move-validation?targetProjectId=${encodeURIComponent(targetProjectId!)}`;
      const response = await fetch(url, { 
        credentials: 'include'
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to validate pulse move');
      }
      return response.json();
    },
    enabled: open && !!targetProjectId,
    retry: false,
  });

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setTargetProjectId(null);
      setSearchQuery("");
      setShowDeleteConfirm(false);
      setShowMoveConfirm(false);
    }
  }, [open]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('DELETE', `/api/pulses/${pulse.id}`);
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete pulse');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects', pulse.projectId] });
      toast({
        title: "Pulse Deleted",
        description: "The pulse and all related data have been removed.",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const moveMutation = useMutation({
    mutationFn: async (targetId: string) => {
      const response = await apiRequest('POST', `/api/pulses/${pulse.id}/move`, {
        targetProjectId: targetId,
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to move pulse');
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects', pulse.projectId] });
      if (data?.targetProjectId) {
        queryClient.invalidateQueries({ queryKey: ['/api/projects', data.targetProjectId] });
      }
      toast({
        title: "Pulse Moved",
        description: `Pulse successfully moved to project ${data?.targetProjectId || targetProjectId}.`,
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    deleteMutation.mutate();
    setShowDeleteConfirm(false);
  };

  const handleMove = () => {
    if (!targetProjectId) return;
    setShowMoveConfirm(true);
  };

  const confirmMove = () => {
    if (!targetProjectId) return;
    moveMutation.mutate(targetProjectId);
    setShowMoveConfirm(false);
  };

  const projectsToShow = searchQuery.length >= 2 ? searchResults : recentProjects;
  const isLoadingProjects = searchQuery.length >= 2 ? loadingSearch : loadingProjects;

  // Filter out current project
  const availableProjects = projectsToShow.filter(p => p.id !== pulse.projectId);

  const canMove = validation?.canMove ?? false;
  const blockers = validation?.blockers ?? [];
  const warnings = validation?.warnings ?? [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] navy-card border-gray-600">
          <DialogHeader>
            <DialogTitle className="text-white">Pulse Settings</DialogTitle>
            <DialogDescription className="text-gray-400">
              Manage settings for: {pulse.pulseDescription?.substring(0, 60)}
              {(pulse.pulseDescription?.length ?? 0) > 60 ? '...' : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Move Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-200 flex items-center gap-2">
                <ArrowRight className="w-4 h-4" />
                Move to Another Project
              </h3>

              {/* Project Search - Always show first */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-gray-800 border-gray-600 text-white"
                  data-testid="input-project-search"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Project List */}
              <div className="max-h-[200px] overflow-y-auto space-y-1 bg-gray-800/50 rounded-md p-2">
                {isLoadingProjects ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : availableProjects.length === 0 ? (
                  <div className="text-sm text-gray-400 py-2 text-center">
                    {searchQuery ? 'No projects found' : 'No recent projects'}
                  </div>
                ) : (
                  availableProjects.map((project) => (
                    <div
                      key={project.id}
                      onClick={() => setTargetProjectId(project.id)}
                      className={cn(
                        "flex items-center gap-3 p-2 rounded cursor-pointer transition-colors text-sm",
                        targetProjectId === project.id
                          ? "bg-blue-600 text-white"
                          : "bg-gray-700/50 hover:bg-gray-600/50 text-gray-200"
                      )}
                      data-testid={`project-option-${project.id}`}
                    >
                      <span className="font-mono text-xs">{project.id}</span>
                      <span className="truncate">{project.projectName}</span>
                      <span className="text-xs text-gray-400 ml-auto">{project.clientName}</span>
                    </div>
                  ))
                )}
              </div>

              {/* Validation Status - Only show when target project is selected */}
              {targetProjectId && loadingValidation ? (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Checking if pulse can be moved...
                </div>
              ) : targetProjectId && validationError ? (
                <div className="bg-red-900/20 border border-red-700 rounded-md p-3 space-y-2">
                  <div className="flex items-center gap-2 text-red-400 text-sm font-medium">
                    <AlertTriangle className="w-4 h-4" />
                    Validation failed
                  </div>
                  <p className="text-sm text-red-300">
                    {(validationErrorDetails as Error)?.message || 'Unable to check if pulse can be moved. Please try again.'}
                  </p>
                  <Button
                    onClick={() => refetchValidation()}
                    variant="outline"
                    size="sm"
                    className="mt-2 text-xs"
                  >
                    Retry
                  </Button>
                </div>
              ) : targetProjectId && !canMove && blockers.length > 0 ? (
                <div className="bg-red-900/20 border border-red-700 rounded-md p-3 space-y-2">
                  <div className="flex items-center gap-2 text-red-400 text-sm font-medium">
                    <AlertTriangle className="w-4 h-4" />
                    Cannot move this pulse
                  </div>
                  <ul className="text-sm text-red-300 space-y-1 ml-6">
                    {blockers.map((blocker, idx) => (
                      <li key={idx} className="list-disc">{blocker.reason}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-gray-400 mt-2">
                    To move this pulse, please delete and recreate it manually in the target project.
                  </p>
                </div>
              ) : targetProjectId && canMove ? (
                <>
                  {/* Warnings */}
                  {warnings.length > 0 && (
                    <div className="bg-yellow-900/20 border border-yellow-700 rounded-md p-3 space-y-1">
                      <div className="text-yellow-400 text-sm font-medium">Moving will also transfer:</div>
                      <ul className="text-sm text-yellow-300 space-y-1 ml-4">
                        {warnings.map((warning, idx) => (
                          <li key={idx} className="list-disc">{warning.message}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Move Button */}
                  <Button
                    onClick={handleMove}
                    disabled={!targetProjectId || moveMutation.isPending}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="button-move-pulse"
                  >
                    {moveMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Moving...
                      </>
                    ) : (
                      <>
                        <ArrowRight className="w-4 h-4 mr-2" />
                        Move Pulse to {targetProjectId || 'Selected Project'}
                      </>
                    )}
                  </Button>
                </>
              ) : null}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-700" />

            {/* Delete Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-200 flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-400" />
                Danger Zone
              </h3>
              <p className="text-xs text-gray-400">
                Deleting a pulse will permanently remove it along with all tasks, time entries, and chat messages.
              </p>
              <Button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                variant="destructive"
                className="w-full"
                data-testid="button-delete-pulse"
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Pulse
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="navy-card border-gray-600">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Pulse?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will permanently delete the pulse "<strong>{pulse.pulseDescription?.substring(0, 50)}</strong>" 
              and all associated tasks, time entries, and chat messages. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-700 hover:bg-gray-600 text-white border-gray-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="button-confirm-delete"
            >
              Delete Pulse
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move Confirmation */}
      <AlertDialog open={showMoveConfirm} onOpenChange={setShowMoveConfirm}>
        <AlertDialogContent className="navy-card border-gray-600">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirm Move</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will move the pulse from <strong>{pulse.projectId}</strong> to <strong>{targetProjectId}</strong>.
              All tasks, time entries, and chat messages will be moved along with the pulse.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-700 hover:bg-gray-600 text-white border-gray-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmMove}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="button-confirm-move"
            >
              Move Pulse
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
